import { useLayoutEffect, useRef } from 'react'
import type { JSX } from 'react'

import { Button } from '@buffer-mono/popcorn'

import { NoteRichEditor } from '~publish/components/Notes'
import type { CollaborationNote } from '~publish/hooks/notes/notesQueries'
import type { UpdateNoteFailure } from '~publish/hooks/notes/useUpdateNote'

import { NotesDismissWarn } from './NotesDismissWarn'
import { usePostNotesContext } from './PostNotesProvider'

const ERROR_COPY: Record<UpdateNoteFailure, string> = {
  validation: 'Enter a Note with no more than 500 characters.',
  permission: 'You do not have permission to edit this Note.',
  request: 'The Note could not save. Try again.',
}

export function EditPostNoteForm({
  note,
}: {
  note: CollaborationNote
}): JSX.Element {
  const { edit, mentionScope } = usePostNotesContext()
  const { draft, pending, enabled, errorFor, change, cancel, submit } = edit
  const error = errorFor(note.id)
  const rootRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    rootRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [])

  return (
    <div ref={rootRef}>
      <NotesDismissWarn>
        <NoteRichEditor
          initialContent={
            draft?.content ?? {
              text: note.text,
              annotations: [...note.annotations],
            }
          }
          onChange={change}
          onSubmit={(content) => void submit(note, content)}
          mentionScope={mentionScope}
          pending={pending}
          submitBlocked={!enabled}
        >
          <NoteRichEditor.Field label="Edit note" focusOnMount />
          <NoteRichEditor.Footer>
            <NoteRichEditor.Formatting />
            <NoteRichEditor.Actions>
              <NoteRichEditor.Counter />
              <Button
                type="button"
                variant="tertiary"
                size="small"
                aria-label="Cancel edit"
                disabled={pending}
                onClick={cancel}
              >
                Cancel
              </Button>
              <NoteRichEditor.Save />
            </NoteRichEditor.Actions>
          </NoteRichEditor.Footer>
          {error && (
            <NoteRichEditor.Error>{ERROR_COPY[error]}</NoteRichEditor.Error>
          )}
        </NoteRichEditor>
      </NotesDismissWarn>
    </div>
  )
}
